<template>
  <div class="ride-booking-panel">
    <div class="wrapper">
      <div v-if="showKeepTrying" class="keep-trying">
        <span class="button is-success" @click="retry">
          {{ $t("passenger.keep-trying") }}&nbsp;
          <i class="fa fa-redo-alt" />
        </span>
        <span
          class="button is-rounded is-white acknowledge"
          @click="acknowledge"
        >
          <i class="fa fa-times-circle" />
        </span>
      </div>

      <div v-if="order">
        <div
          class="header is-size-5"
          @click="updateTransactionsForCustomer(false)"
        >
          <div style="width: calc(100vw - 40px)">
            {{ header.title }}
            <i :class="header.icon" />
          </div>
          <div v-if="header.scheduled">
            <span class="tag is-info semibold">
              Scheduled pickup time: {{ header.scheduled }}
            </span>
          </div>

          <span
            v-if="showHeightToggle"
            class="toggle-btn"
            @click="toggleHeight(null)"
          >
            <zoom-x-transition group :duration="1200">
              <i class="fa fa-chevron-down" v-if="isTall" :key="2" />
              <i class="fa fa-chevron-up" v-else :key="1" />
            </zoom-x-transition>
          </span>
        </div>

        <div class="body">
          <div class="card" v-if="hint">
            <div class="card-content has-text-left single-line no-scrollbar">
              <i class="l-icon far fa-comment has-text-grey" />
              <i v-html="hint" />
            </div>
          </div>
          <div class="card">
            <div class="card-content">
              <div style="display: flex">
                <div class="left">
                  <p><i class="fa fa-circle has-text-info" /></p>
                  <p>
                    <i class="fa fa-ellipsis-v fa-sm" style="color: grey" />
                  </p>
                  <p><i class="fa fa-map-marker-alt has-text-success" /></p>
                </div>
                <div class="right has-text-left">
                  <p class="clamp" style="height: 48px">
                    {{ $t("driver.pickup-at") }}
                    <a :href="googleMapPickup" target="_blank">
                      {{ pickupName }}
                      <!-- <i class="fa fa-directions fa-lg" /> -->
                    </a>
                  </p>
                  <p v-if="t.data.dropoff_stop">
                    {{ $t("driver.dropoff-at") }}
                    <a :href="googleMapDropoff" target="_blank">
                      {{ dropoffName }}
                      <!-- <i class="fa fa-directions fa-lg" /> -->
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="card">
            <div class="card-content has-text-left">
              <p>
                <i class="l-icon fa fa-coins has-text-grey" />
                <i class="fare-word">{{ $t("fare") }}</i>
                <span v-if="isFree" class="free-word">
                  {{ $t("free") }}
                </span>
                <span v-else>
                  <span class="fare-amount has-text-link">
                    {{ currency }}{{ cashFare }}
                  </span>
                  <span class="button is-light">{{ $t("cash") }}</span>
                </span>

                <!--<span class="tag is-dark is-medium">{{ $t("free") }}</span>
                <span class="tag is-grey is-medium has-text-weight-semibold">
                 <img src="../assets/tukpay.png" style="height: 16px" />
                </span>-->

                <span
                  v-if="isDiscounted"
                  class="tag has-text-primary discount-applied"
                >
                  {{ $t("passenger.discount-applied") }}
                  <span
                    v-if="discount"
                    style="margin-left: 4px; font-weight: 550"
                  >
                    -{{ currency }}{{ discount }}
                  </span>
                </span>
              </p>
            </div>
          </div>

          <collapse-transition>
            <div v-show="isTall">
              <RideBookingDriver v-if="t.driver" :driver="t.driver" />

              <div class="card" v-if="showTransportButtons">
                <div class="card-content">
                  <div class="buttons is-centered">
                    <button
                      class="button is-grey is-rounded"
                      v-if="t.state === 'enroute' || agentMode"
                      @click="endTrip"
                    >
                      <span class="icon">
                        <i class="fa fa-times-circle" />
                      </span>
                    </button>
                    <button
                      class="button is-warning is-rounded"
                      v-if="t.state === 'enroute'"
                      @click="shareTUKApp"
                    >
                      <i class="fas fa-share-alt" />
                    </button>
                    <!-- class="button is-danger is-rounded is-outlined" -->
                    <span
                      class="link cancel-btn"
                      v-if="t.state !== 'enroute'"
                      @click="cancel"
                    >
                      {{ $t("cancel") }}
                    </span>
                  </div>
                </div>
                <div class="card" v-if="showAcknowledgeButton">
                  <div class="card-content">
                    <div class="buttons is-centered">
                      <button
                        class="button is-danger is-rounded"
                        @click="acknowledge"
                      >
                        {{ $t("okay") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </collapse-transition>

          <collapse-transition>
            <div class="card" v-show="!isTall">
              <div v-if="showHeightToggle" @click="toggleHeight(null)">
                <i class="fa fa-ellipsis-h" style="padding: 12px" />
              </div>
              <div v-else-if="t.state === 'initiated'" class="card-content">
                <!-- <button class="button is-danger is-rounded" @click="cancel">
                  {{ $t("cancel") }}
                </button> -->
                <span class="link cancel-btn" @click="cancel">
                  {{ $t("cancel") }}
                </span>
              </div>
            </div>
          </collapse-transition>

          <div v-if="showMarquee" class="card">
            <marquee-text :duration="3">
              <div class="marquee" style="width: 100vw; padding-top: 5px">
                <img :src="vehicleIcon" />
              </div>
            </marquee-text>
          </div>

          <RideBookingRating
            v-if="t.state === 'alighted'"
            :t="t"
            @close="acknowledge"
          />
        </div>
      </div>

      <div v-else>
        <div class="header is-size-5">
          <span>{{ $t("ride.fetching-booking-details") }}</span
          >&nbsp;
          <i class="fa fa-spinner fa-spin has-text-primary" />
        </div>
      </div>
    </div>

    <RideCancelModal :t="t" />
  </div>
</template>

<script>
// import EventBus from "../eventBus";
// import { sounds } from "../functions.js";
import vehicles from "@/vehicles";

import RideCancelModal from "@/components/RideCancelModal.vue";
import RideBookingRating from "@/components/RideBookingRating.vue";
import RideBookingDriver from "@/components/RideBookingDriver.vue";

export default {
  name: "RideBookingPanel",
  components: {
    RideCancelModal,
    RideBookingRating,
    RideBookingDriver,
  },
  props: {
    order: Object,
  },
  watch: {
    order() {
      if (this.order && this.order.state !== this.state) {
        this.toggleHeight({ force: true }); // force true
        this.state = this.order.state;
      }
    },
  },
  data() {
    return {
      isTall: false,
      state: "initiated", // track state change for height toggle
    };
  },
  computed: {
    t() {
      return this.order;
    },
    businessName() {
      return this.order.business.name;
    },
    role() {
      return this.$store.getters.getDriverRole;
    },
    agentMode() {
      return this.$store.state.agentMode;
    },
    pickupName() {
      return this.t.data.pickup_stop.name || this.$t("ride.pin-location");
      //   return this.shortenString(this.t.data.pickup_stop.name, 40);
    },
    dropoffName() {
      return this.t.data.dropoff_stop.name || this.$t("ride.pin-location");
      //   return this.shortenString(this.t.data.dropoff_stop.name, 40);
    },
    googleMapPickup() {
      var a = this.t.data.pickup_stop;
      return `http://www.google.com/maps/place/${a.lat},${a.lon}`;
    },
    googleMapDropoff() {
      var a = this.t.data.dropoff_stop;
      return `http://www.google.com/maps/place/${a.lat},${a.lon}`;
    },
    isDiscounted() {
      if (this.t.workflow_type_name === "Private Tuk Tuk") return false;
      return true; // not for private..
    },
    isFree() {
      return !this.t.data.fare || !this.t.data.fare.cash;
    },
    cashFare() {
      return this.t.data.fare.cash;
    },
    discount() {
      return this.t.data.fare.discount;
    },
    // orderValue() {
    //   return 100;
    // },
    currency() {
      return this.currencySymbol(this.order.business.country);
    },
    pickupTime() {
      return this.shortDateTime(this.order.data.scheduled_time);
    },
    humanTimeOrTimeAgo() {
      var t = this.order;
      if (this.humanTime(t) !== "Passed") return this.humanTime(t);
      return this.timeAgo(t.data.scheduled_time);
    },
    updatedTimeAgo() {
      return this.timeAgo(this.order.updated_at);
    },
    minsAgo() {
      var minsAgo = (new Date() - new Date(this.t.updated_at)) / (1000 * 60);
      // if (minsAgo < 90) return `76 mins ago` // e.g.
      // 1.6 hours ago
      return Math.round(minsAgo);
    },
    header() {
      var o = {};
      var timeString = this.scheduledTime(this.t);
      switch (this.t.state) {
        case "initiated":
          o.title = this.$t("transport.waiting-for-confirmation");
          o.icon = "fa fa-spinner fa-spin has-text-grey";
          o.scheduled = timeString;
          break;
        case "confirmed":
          o.title = this.$t("passenger.driver-on-the-way");
          o.icon = "fa fa-thumbs-up has-text-grey";
          o.scheduled = timeString;
          break;
        case "driver_arrived":
          o.title = this.$t("passenger.driver-has-arrived");
          o.icon = "fa fa-map-pin has-text-grey";
          o.scheduled = timeString;
          break;
        case "enroute":
          o.title = this.$t("passenger.welcome-aboard");
          o.icon = "fa fa-glass-cheers has-text-grey";
          break;
        case "alighted":
          o.title = this.$t("passenger.good-trip");
          o.icon = "fa fa-smile-beam has-text-grey";
          break;
        case "rejected":
          o.title = this.$t("passenger.driver-rejected");
          o.icon = "fa fa-frown has-text-grey";
          break;
        case "cancelled_by_driver":
          o.title = this.$t("passenger.driver-cancelled");
          o.icon = "fa fa-frown-open has-text-grey";
          break;
        case "no_show":
          o.title = this.$t("passenger.no-show");
          o.icon = "fa fa-frown-open has-text-grey";
          break;
        case "timed_out":
          (o.title = this.$t("passenger.drivers-busy")),
            (o.icon = "fa fa-frown-open has-text-grey");
          break;
        case "cancelled_by_passenger":
          o.title = this.$t("ride.cancelling");
          o.icon = "fa fa-spinner fa-spin has-text-grey";
          break;
      }
      return o;
    },
    hint() {
      switch (this.t.state) {
        case "initiated":
          return this.$t("ride.looking-for-nearby-driver");
        case "confirmed":
          return this.$t("passenger.please-walk");
        case "driver_arrived":
          var x = { x: this.vehicleNumber };
          return this.$t("ride.vehicle-x-is-at-pickup", x);
        case "enroute":
          return this.$t("ride.enjoy-your-ride");
        case "alighted":
          return this.$t("ride.thanks-for-riding") + " 🙏🏽";
        case "timed_out":
          return "You can try again...";
        case "rejected":
          return "The fleet is not operating at the moment";
      }
      return null;
    },
    vehicleNumber() {
      return this.t.driver.role.data.vehicle_number;
    },
    showPleaseWalk() {
      var states = ["confirmed", "driver_arrived"];
      return states.includes(this.t.state);
    },
    showTransportButtons() {
      var transport = ["initiated", "confirmed", "driver_arrived", "enroute"];
      return transport.includes(this.t.state);
    },
    showAcknowledgeButton() {
      var states = ["rejected", "cancelled_by_driver", "no_show"];
      return states.includes(this.t.state);
    },
    showHeightToggle() {
      var states = [
        "timed_out",
        "initiated",
        "cancelled_by_passenger",
        "cancelled_by_driver",
        "no_show",
        "rejected",
      ]; // "rejected", "cancelled_by_driver", "no_show",
      return !states.includes(this.t.state);
    },
    showKeepTrying() {
      if (!this.t) return false;
      var states = ["timed_out", "cancelled_by_driver", "no_show", "rejected"]; // "rejected"
      return states.includes(this.t.state);
    },
    showMarquee() {
      if (this.showKeepTrying) return true;
      if (this.t.state === "cancelled_by_passenger") return true;
      return false;
    },
    vehicleIcon() {
      if (!this.t.workflow) return null;
      var iconName = this.t.workflow.data.icon || this.t.workflow.name;
      return vehicles.img(iconName);
    },
  },
  created() {},
  mounted() {},
  activated() {},
  beforeDestroy() {},
  methods: {
    cancel() {
      this.displayModal(".ride-cancel-modal");
    },
    destroy() {
      this.gotoDelay("ride", 1000);
      setTimeout(() => {
        if (this.$route.name === "ride-booking") {
          this.goto("/ride");
          //   this.$destroy();
        }
      }, 2000); // destroy the /RideBooking page
    },
    endTrip() {
      this.$store.commit("endTransaction", this.t.id);
      //   this.updateTransactionsForCustomer(true);
      this.updateTrips(true);
      this.destroy();
    },
    retry() {
      var loader = this.loadingDots();
      this.$store
        .dispatch("patchTransaction", {
          transactionID: this.t.id,
          data: { state: "initiated", data: { retry: true } },
        })
        .then((res) => {
          this.updateTransactionsForCustomer(true);
          this.goto("/ride_booking/" + res.data.id); // transaciton id
          this.$emit("update");
        })
        .catch((err) => this.apiError(err))
        .finally(() => loader.hide());
    },
    acknowledge() {
      this.$store.dispatch("getRideCount"); // update ride count
      //   this.$store.commit("selectDriver", null); // turn off dispatch mode
      // var loader = this.loadingDots();
      this.$store
        .dispatch("patchTransaction", {
          transactionID: this.t.id,
          data: { state: "acknowledged" },
        })
        .catch((err) => this.apiError(err))
        .finally(() => {
          // loader.hide();
          this.updateTrips(true); //   this.updateTransactionsForCustomer(true);
          this.destroy();
        });
    },
    toggleHeight(val) {
      console.log("val", val);
      if (val) {
        if (val.force) this.isTall = true;
        else this.isTall = false;
      } else {
        this.isTall = !this.isTall;
      }
      this.$emit("toggleHeight", this.isTall);
    },
    transactionUpdatedInfo() {
      var ago = this.timeAgo(this.t.updated_at);
      this.infoToast(`Transaction updated ${ago}`);
    },
    driverUpdatedInfo() {
      var ago = this.timeAgo(this.t.driver.user_state.updated_at);
      this.infoToast(`Driver last updated ${ago}`);
    },
    callBusiness() {
      this.callPhoneNumber(this.order.business.phone_number);
    },
    shareOrder() {
      var shortID = this.order.data.delivery.short_link;
      if (!shortID) return this.errorToast("No shareable link!", "top");
      var url = process.env.VUE_APP_HOST;
      var shortLink = `${url}/q/${shortID}`;
      this.$copyText(shortLink);
      this.infoToast("Share Job, Link Copied", "top"); // copied to clipboard
      if (!navigator.share) {
        var txt = `Sharing not supported by this device.<br><br>Send as SMS or paste into a chat.`;
        return this.warningAlert(txt);
      }
      var msg = this.$t("TUK Logistics📦 Click link to see your order!🎉");
      navigator
        .share({ text: msg, url: shortLink })
        .then(() => console.log("Successful share"))
        .catch((err) => console.log("Error sharing", err));
    },
  },
};
</script>

<style scoped>
.wrapper {
  z-index: 1;
  position: absolute;
  bottom: 0px;
  left: 0px;
  height: auto;
  min-height: 255px;
  max-height: 80vh;
  width: 100%;
}
.header {
  padding: 6px;
  padding-left: 12px;
  /* background-color: #f5d252; */
  background-color: rgb(233, 233, 233);
  /* border-top-right-radius: 10px;
  border-top-left-radius: 10px; */
  box-shadow: 0px -1px 1px rgba(100, 100, 100, 0.2);
  text-align: left;
}
.column.tab {
  padding-right: 0;
}
@keyframes shadow-pulse {
  0% {
    box-shadow: 0 0 0 0px rgba(247, 211, 8, 0.95);
  }
  100% {
    box-shadow: 0 0 0 10px rgba(247, 211, 8, 0.5);
  }
}
.pulse {
  animation: shadow-pulse 1s infinite;
  border-radius: 50%;
}
.card-content {
  padding: 12px;
}
.share-btn {
  padding: 5px;
  font-size: 10pt;
  box-shadow: 1px 1px 1px grey;
}
/* img {
  height: 75px;
  width: 75px;
  border-radius: 3px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.14);
  object-fit: cover;
} */
.pad {
  width: 16px;
  margin-right: 12px;
}
/* new ride_booking */
.left {
  height: 80px;
  width: 30px;
}
.right {
  padding-left: 10px;
  width: calc(100vw - 30px);
}
.toggle-btn {
  position: absolute;
  right: 0;
  top: -2px;
  /* top: 2px; */
  margin-right: 4px;
  padding: 10px;
  /* color: grey; */
  color: white;
  text-shadow: 0 0 1px black;
}
.fare-word {
  display: inline-block;
  margin: 6px 0;
  font-weight: 500;
}
.fare-amount {
  display: inline-block;
  margin: 6px 10px;
  font-weight: 550;
}
.free-word {
  display: inline-block;
  margin: 6px 10px;
  font-weight: 550;
  color: #00d1b2;
  text-transform: uppercase;
}
.discount-applied {
  float: right;
  margin-top: 6px;
  font-weight: 500;
}
.l-icon {
  display: inline-block;
  width: 30px;
  margin-right: 10px;
  text-align: center;
}
.semibold {
  font-weight: 600;
}
.purple {
  color: purple;
}
.orange {
  color: orange;
}
.clamp {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  /* padding-right: 4px; */
}
.single-line {
  white-space: nowrap;
  overflow: auto;
}
.keep-trying {
  position: absolute;
  top: -44px;
  text-align: center;
  width: 100vw;
}
.keep-trying .button {
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.5);
}
.marquee img {
  height: 40px;
  width: 40px;
  transform: scaleX(-1);
}
.marquee .tag {
  margin-left: 20px;
}
.cancel-btn {
  margin-bottom: 8px;
  font-weight: 500;
  opacity: 0.5;
  color: var(--danger);
}
.acknowledge.button {
  padding: 9px;
  position: absolute;
  right: 9px;
}
</style>